import type { UserRole } from '@inou/shared';
import { NavLink } from 'react-router';

import { Logo } from '@/components/brand/Logo';
import { UserMenu } from '@/components/layout/UserMenu';
import { type AppRoute, canOpen, ROUTES } from '@/lib/nav';
import { cn } from '@/lib/utils';

/**
 * ナビはテナントのものだけ。個人に紐付くもの (アカウント・MCP接続) は
 * 下の UserMenu へ置く。同じ並びにあると全社の設定に見えてしまう。
 *
 * 表示名も出す条件も `lib/nav.ts` の経路の表が持つ。ルーターのガードと
 * 画面間のリンクが同じ表を見るので、ここだけ条件がずれることがない。
 *
 * 先頭の「はじめに」だけは、出す条件がロールではなくデータ (そのテナントに
 * まだ工数が無いか) なので、`AppLayout` が読んだ結果を prop で受け取る。
 * 最初の 1 件が記録されると黙って消える。
 */
const SECTIONS: Array<{ label: string; items: AppRoute[] }> = [
  {
    label: 'MANAGE',
    items: [
      ROUTES.utilization,
      ROUTES.capitalization,
      ROUTES.assignments,
      ROUTES.timeLogs,
      ROUTES.projects,
      ROUTES.members,
    ],
  },
  {
    // 監査ログは全社の設定と同じ性質で、日々開く画面ではないのでここに置く
    label: 'SETTINGS',
    items: [ROUTES.tenantSettings, ROUTES.auditLogs],
  },
];

export interface SidebarProps {
  userName: string;
  /** 表示用のロール名 */
  userRoleLabel: string;
  role: UserRole;
  /** 開通直後の案内への導線を出すか (テナントにまだ工数が無い間だけ) */
  showStart: boolean;
  onSignedOut: () => void;
  /** 768px 未満でドロワーが開いているか。以上では常に出る。 */
  open: boolean;
  /** ドロワーの中から遷移したときに閉じてもらう */
  onNavigate: () => void;
}

export function Sidebar({
  userName,
  userRoleLabel,
  role,
  showStart,
  onSignedOut,
  open,
  onNavigate,
}: SidebarProps) {
  return (
    /*
     * 768px 以上は今までどおり流れの中の縦の帯。768px 未満では本文に幅を返すため、
     * 画面外に置いて `☰` で重ねて出す。
     *
     * 畳んでいる間は `hidden` にする (画面外に平行移動したままにすると、
     * 見えないリンクにタブで到達できてしまう)。`md:` 側で `static` に戻すので、
     * `fixed inset-y-0 left-0 z-40` は 768px 以上では効かない
     * (静的配置では top/left も z-index も無視される)。
     */
    <aside
      id="app-sidebar"
      className={cn(
        'fixed inset-y-0 left-0 z-40 w-58 shrink-0 flex-col overflow-y-auto border-r border-line bg-surface-raised py-5',
        'md:static md:z-auto md:flex md:overflow-y-visible',
        open ? 'flex' : 'hidden',
      )}
    >
      <div className="px-5 pb-6">
        <Logo height={36} className="text-brand-500" />
      </div>

      <nav className="flex flex-col gap-0.5 px-3">
        {showStart && canOpen(role, ROUTES.start) ? (
          <NavLink
            to={ROUTES.start.path}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'mb-2 border border-line px-2.5 py-2.5 text-sm',
                isActive ? 'bg-brand-700 text-surface-raised' : 'text-ink-muted hover:text-ink',
              )
            }
          >
            {ROUTES.start.label}
          </NavLink>
        ) : null}

        {SECTIONS.map((section) => {
          const items = section.items.filter((item) => canOpen(role, item));
          if (items.length === 0) return null;

          return (
            <div key={section.label} className="flex flex-col gap-0.5">
              <span className="px-2.5 py-2 font-mono text-[11px] tracking-wider text-ink-faint">
                {section.label}
              </span>
              {items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'px-2.5 py-2.5 text-sm',
                      isActive
                        ? 'bg-brand-700 text-surface-raised'
                        : 'text-ink-muted hover:text-ink',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <UserMenu
        userName={userName}
        userRoleLabel={userRoleLabel}
        onSignedOut={onSignedOut}
        onNavigate={onNavigate}
      />
    </aside>
  );
}
